import './statics.scss';
import { useContext } from 'react';
import Button from 'components/button';
import { INE_URL } from 'constants/domains';
import { INE_CONTACT_SALES } from 'constants/settings';
import { PLAN_ENTERPRISE_PLUS_LAB, PLAN_LAB_EXPERIENCE } from 'constants/plans_config';
import { SessionContext } from 'contexts/session';
import {
  CONTACT_SALES_CTA,
  CONTENT_TYPES,
  PREVIEW_COLLECTION_CTA,
  SKILL_DIVE_COLLECTION_TAG,
} from 'constants/playlists';
import { stripHtml } from 'utils/helpers';

export interface IPreviewCollectionData {
  objectID: string;
  description?: string;
  summary?: { total_labs?: number };
  total_labs?: number;
  total_flags?: number;
}

const PreviewCollection = (props: { data: IPreviewCollectionData }): JSX.Element => {
  const { objectID, description, summary, total_labs, total_flags } = props.data;
  const { account, hasSkillDive: sessionHasSkillDive } = useContext(SessionContext);
  const overview = description ? stripHtml(description) : '';

  // The index exposes the lab count under summary today; total_labs/total_flags
  // arrive as top-level fields with the backend contract (DEV-5872).
  const labCount = total_labs ?? summary?.total_labs ?? 0;
  const flagCount = total_flags ?? 0;

  const hasLabExperience =
    account?.pricing_plan === PLAN_LAB_EXPERIENCE ||
    account?.pricing_plan === PLAN_ENTERPRISE_PLUS_LAB;
  // The session resolves the add-on by id; the name match covers payloads that
  // only carry the display name.
  const hasSkillDive =
    Boolean(sessionHasSkillDive) ||
    account?.account_available_addons?.some((addon) => addon.name === CONTENT_TYPES.SKILL_DIVE);
  const needsUpsell = !hasLabExperience && !hasSkillDive;

  const handleDetails = () => {
    window.open(`${INE_URL}/collections/${objectID}`, '_blank', 'noopener,noreferrer');
  };

  const handleContact = () => {
    window.location.href = `mailto:${INE_CONTACT_SALES},${account?.sales_contact?.email}`;
  };

  return (
    <div className="preview-tooltip">
      {overview && (
        <p className="preview-tooltip__description">
          <b>Overview: </b>
          {overview.length > 600 ? `${overview.slice(0, 600)}...` : overview}
        </p>
      )}

      <div className="preview-tooltip__info-container">
        <div className="preview-tooltip__info-row">
          <div className="preview-tooltip__info-column">LAB TYPE</div>
          <div className="preview-tooltip__info-column">{SKILL_DIVE_COLLECTION_TAG}</div>
        </div>
        <div className="preview-tooltip__info-row">
          <div className="preview-tooltip__info-column">ACTIVITIES</div>
          <div className="preview-tooltip__info-row">
            <div className="preview-tooltip__activity-amount-tag">LABS: {labCount}</div>
            <div className="preview-tooltip__activity-amount-tag">FLAGS: {flagCount}</div>
          </div>
        </div>
      </div>

      <div className="preview-tooltip__button-container">
        {needsUpsell ? (
          <Button icon="Envelope" $secondary size="small" onClick={handleContact}>
            {CONTACT_SALES_CTA}
          </Button>
        ) : (
          <Button icon="ExternalLink" $secondary size="small" onClick={handleDetails}>
            {PREVIEW_COLLECTION_CTA}
          </Button>
        )}
      </div>
    </div>
  );
};

export default PreviewCollection;
